import type {MouseEvent, ReactElement} from 'react';
import {useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize, TextType} from '@Components/text';
import {useAudioPlayback} from '@Hooks/ai-text-to-speech/useAudioPlayback';
import {getRandomThumbnailImageForAudioItem} from '@Libraries/poster-audio-item.library';
import {ProgressPlayPauseButton} from '@Components/progress-play-pause-button/progress-play-pause-button';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {numberToMMSSFormat} from '@Utils/date.util';
import type {ReelMusicTrackItemProps} from './reel-music-track-item.types';
import styles from './reel-music-track-item.module.scss';

export function ReelMusicTrackItem({track, isSelected, onSelectTrack}: ReelMusicTrackItemProps): ReactElement {
  const audioPlayback = useAudioPlayback();
  const [artworkUrl] = useState(getRandomThumbnailImageForAudioItem);
  const titleColorClass = isSelected ? 'ct-brand-primary' : 'ct-neutral-primary';

  const getArtwork = (): ReactElement => {
    return (
      <div className={`flex-center _position-relative radius-8 ${styles.artworkContainer}`}>
        <img className={`radius-8 ${styles.artwork}`} src={artworkUrl} alt="" loading="lazy" />
      </div>
    );
  };

  const getTitleAndDetails = (): ReactElement => {
    return (
      <div className={`flexbox flex-1 flex-column ${styles.textContainer}`}>
        <Text className={`-truncated-text ${titleColorClass}`} bold size={TextSize.MD} type={TextType.BODY} val={track.title} />
        <div className="flexbox flex-items-center gap-1">
          <Text size={TextSize.SM} className={`-truncated-text ${styles.secondaryText}`} val={track.artist} />
          <span className={styles.separatingDot} aria-hidden />
          <Text size={TextSize.SM} className={styles.secondaryText} val={numberToMMSSFormat(track.durationSeconds)} />
        </div>
      </div>
    );
  };

  const getPreviewButton = (): ReactElement | null => {
    const {previewUrl} = track;

    if (!previewUrl) {
      return null;
    }

    return (
      <ProgressPlayPauseButton
        isPlaying={audioPlayback.isPlaying}
        playbackProgress={audioPlayback.playbackProgress}
        onClick={(event: MouseEvent): void => {
          event.stopPropagation();
          event.preventDefault();
          audioPlayback.togglePlayPause(previewUrl);
        }}
        baseColor={PMW_COLORS_NEUTRAL.NEUTRAL_6}
        className={styles.playButton}
      />
    );
  };

  return (
    <ClickableDiv
      role="option"
      aria-selected={isSelected}
      className={`flex-center radius-8 gap-2 spacing-p-t-1 spacing-p-b-1 spacing-p-l-1 spacing-p-r-2 _cursor-pointer hover-transition-all ${styles.trackItem} ${isSelected ? styles.trackItemSelected : ''}`}
      onClick={(): void => {
        onSelectTrack(track);
      }}
    >
      {getArtwork()}
      {getTitleAndDetails()}
      {getPreviewButton()}
    </ClickableDiv>
  );
}
